import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { FaClock, FaAngleRight, FaAngleLeft, FaCalendar, FaUser, FaExpand } from "react-icons/fa";
import Slider from "react-slick";
import FsLightbox from "fslightbox-react";
import { useDispatch } from "react-redux";
import SEO from "../common/SEO";
import HeaderOne from "../common/header/HeaderOne";
import SplashFooter from "../common/footer/SplashFooter";
import NewsSidebar from "../component/news/NewsSidebar";
import NewsGrid from "../component/news/NewsGrid";
import ColorSwitcher from "../elements/switcher/ColorSwitcher";
import { media_url, useBlog } from "../hooks/useBlog";
import { setBlogItems } from "../store/store";
import { logPageView, logEvent, logTiming } from "../services/analytics";
import BlogAttachments from "../component/blog/BlogAttachments";
import './BlogDetails.scss';

const BlogDetails = () => {
  const query = new URLSearchParams(window.location.search);
  const params = useParams();
  const [viewState, setViewState] = useState({
    post: null,
    loading: true,
  });
  const { getPost, getPosts } = useBlog();
  const dispatch = useDispatch();
  const [pageStartTime] = useState(Date.now());
  const [relatedPosts, setRelatedPosts] = useState([]);
  const [lightboxController, setLightboxController] = useState({
    toggler: false,
    slide: 1
  });

  function SlickNextArrow(props) {
    const { className, onClick } = props;
    return (
      <div className={className} onClick={onClick}>
        <FaAngleRight />
      </div>
    );
  }

  function SlickPrevArrow(props) {
    const { className, onClick } = props;
    return (
      <div className={className} onClick={onClick}>
        <FaAngleLeft />
      </div>
    );
  }

  const slideSettings = {
    dots: false,
    infinite: true,
    speed: 800,
    autoplay: true,
    autoplaySpeed: 5000,
    fade: true,
    slidesToShow: 1,
    slidesToScroll: 1,
    nextArrow: <SlickNextArrow />,
    prevArrow: <SlickPrevArrow />,
  };

  const loadPostFromId = async (id) => {
    setViewState({ ...viewState, loading: true });
    const post = await getPost(id);
    var postBody = post.body;
    // remove all spaces inside > <
    postBody = postBody.replaceAll(/>\s+</g, "><");
    // remove empty tags than match <anyIncludingSpacesAnd="'"></anyIncludingSpaces>
    postBody = postBody.replaceAll(/<([a-zA-Z0-9]+)(\s+[^>]*)?>\s*<\/\1>/g, "");
    setViewState({ post: { ...post, body: postBody }, loading: false });
    dispatch(
      setBlogItems({
        currentPost: {
          ...post,
          body: postBody,
        },
      })
    );
    
    // Registrar pageview en Google Analytics
    logPageView(window.location.pathname, post.title);
    // Registrar evento de lectura de blog
    logEvent('Blog', 'View', post.title, post.id);

    // Cargar artículos relacionados
    const related = await getPosts(1, 6);
    if (related && related.length > 0) {
      setRelatedPosts(related.filter(p => p.id !== post.id).slice(0, 6));
    }
  };

  useEffect(() => {
    if (query.get("id")) {
      const id = query.get("id");
      loadPostFromId(id);
    }
  }, []);

  // listen to query param changes
  useEffect(() => {
    if (query.get("id")) {
      const id = query.get("id");
      loadPostFromId(id);
    }
  }, [query.get("id")]);

  // Registrar tiempo de lectura cuando el usuario abandona la página
  useEffect(() => {
    return () => {
      if (viewState.post) {
        const timeSpent = Math.round((Date.now() - pageStartTime) / 1000);
        logTiming('Blog', 'Reading Time', timeSpent, viewState.post.title);
      }
    };
  }, [viewState.post, pageStartTime]);

  if (viewState.loading || !viewState.post) {
    return (
      <div className="news-article-loading">
        <div className="spinner"></div>
      </div>
    );
  }

  const { post } = viewState;
  const categoryName = typeof post.category === 'object' ? post.category.name : post.category;
  const authorName = post.owner?.fullname || post.author || 'Admin';

  // Preparar imágenes para el lightbox
  const allImages = [];
  if (Array.isArray(post.images) && post.images.length > 0) {
    post.images.forEach(img => allImages.push(media_url(img.image)));
  } else if (post.image) {
    allImages.push(media_url(post.image));
  }
  if (post.features_img && post.features_img.length > 0) {
    post.features_img.forEach(img => allImages.push(media_url(img)));
  }

  const openLightboxOnSlide = (index) => {
    setLightboxController({
      toggler: !lightboxController.toggler,
      slide: index + 1
    });
  };

  return (
    <>
      <SEO
        title={post.title}
        description={post.excerpt}
        keyword={post.keywords}
        author={authorName}
        image={media_url(post.images?.[0]?.image_thumb || post.image)}
      />
      <ColorSwitcher />
      
      <main className="news-article-wrapper">
        <HeaderOne />

        {/* Hero Section con imagen principal */}
        <div className="news-article-hero">
          <div
            className="news-article-hero-image"
            style={{
              backgroundImage: `url(${media_url(post.image || post.images?.[0]?.image)})`
            }}
          >
            <div className="news-article-hero-overlay"></div>
            <div className="news-article-hero-info">
              {categoryName && (
                <span className="news-article-category">{categoryName}</span>
              )}
              <h1 className="news-article-hero-title">{post.title}</h1>
              <div className="news-article-hero-meta">
                <span>
                  <FaUser />
                  {authorName}
                </span>
                <span>
                  <FaCalendar />
                  {post.created}
                </span>
                {post.reading_time && (
                  <span>
                    <FaClock />
                    {post.reading_time} min lectura
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Contenido del artículo */}
        <div className="news-article-container">
          <div className="news-article-content">
            <div className="container-fluid">
              <div className="row">
                <div className="col-lg-8 col-xl-9">
                  <div className="news-article-body">
                    {/* Contenido HTML del post */}
                    <div dangerouslySetInnerHTML={{ __html: post.body }}></div>

                    {/* Galería de imágenes */}
                    {allImages.length > 0 && (
                      <div className="news-article-gallery">
                        <h3 className="gallery-title">
                          Galería de Imágenes ({allImages.length})
                        </h3>
                        <div className="gallery-grid">
                          {allImages.map((img, index) => (
                            <div 
                              key={index} 
                              className="gallery-item"
                              onClick={() => openLightboxOnSlide(index)}
                            >
                              <img src={img} alt={`Galería ${index + 1}`} />
                              <div className="gallery-overlay">
                                <FaExpand className="gallery-icon" />
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Archivos adjuntos */}
                    {post.files && post.files.length > 0 && (
                      <BlogAttachments files={post.files} />
                    )}
                  </div>
                </div>

                {/* Sidebar */}
                <div className="col-lg-4 col-xl-3">
                  <div className="news-article-sidebar">
                    <NewsSidebar />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Artículos relacionados */}
        {relatedPosts && relatedPosts.length > 0 && (
          <div className="news-related-section">
            <div className="container-fluid">
              <div className="section-heading">
                <h2 className="title">Artículos Relacionados</h2>
              </div>
              <NewsGrid posts={relatedPosts} loading={false} />
            </div>
          </div>
        )}

        <SplashFooter />

        {/* Lightbox para galería de imágenes */}
        {allImages.length > 0 && (
          <FsLightbox
            toggler={lightboxController.toggler}
            sources={allImages}
            slide={lightboxController.slide}
            type="image"
          />
        )}
      </main>
    </>
  );
};

export default BlogDetails;
